import React, { useState, useEffect } from 'react';
import { axiosInstance } from '../App';

const OrderPrintThermalSummary = ({ order, customer, showItemRate = true }) => {
  const [termsAndConditions, setTermsAndConditions] = useState([]);

  useEffect(() => {
    loadTermsAndConditions();
  }, []);

  const loadTermsAndConditions = async () => {
    try {
      // Fetch only terms and conditions for orders module
      const res = await axiosInstance.get('/terms-conditions/type/orders');
      const activeTerms = (res.data || []).filter(t => t.is_active !== false);
      setTermsAndConditions(activeTerms);
    } catch (err) {
      console.error('Error loading T&C:', err);
      setTermsAndConditions([]);
    }
  };

  // Calculate totals
  const calculateTotals = () => {
    if (!order || !order.items) {
      return { subtotal: 0, totalAdvance: 0, metalCredit: 0 };
    }

    let subtotal = 0;
    order.items?.forEach(item => {
      const weight = item.weight_type === 'Fixed' 
        ? parseFloat(item.weight_fixed || 0) 
        : (parseFloat(item.weight_min || 0) + parseFloat(item.weight_max || 0)) / 2;
      
      const rate = parseFloat(item.rate || 0);
      const making = parseFloat(item.making_value || 0);
      const quantity = parseFloat(item.quantity || 1);
      
      let itemTotal;
      if (item.making_type === 'WASTAGE') {
        let rate24k = parseFloat(order.base24k_rate_ex_gst || 0);
        if (order.gst_input_style === 'EXCL' && order.tax_scope !== 'KACCHA' && rate24k > 0) {
          rate24k = rate24k * 1.03;
        }
        itemTotal = weight * rate24k * (making / 100);
      } else {
        itemTotal = weight * rate * quantity;
        if (item.making_type === 'PERCENT') {
          itemTotal += itemTotal * (making / 100);
        } else if (item.making_type === 'PER_GRAM') {
          itemTotal += weight * making * quantity;
        } else if (item.making_type === 'FLAT') {
          itemTotal += making * quantity;
        }
      }
      
      subtotal += itemTotal;
    });

    const advancePaid = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') return sum;
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);

    const metalAdvance = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit' && p.advance_equivalent) {
        return sum + parseFloat(p.advance_equivalent);
      }
      return sum;
    }, 0);

    const metalCredit = (order.advance_payments || []).reduce((sum, p) => {
      if (p.mode === 'Metal Credit') {
        // Check if payment has deposits array with details
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        // Fallback to old structure
        if (p.fine_wt) {
          return sum + parseFloat(p.fine_wt);
        }
      }
      // Old Gold should NOT be included in Metal Credit total
      return sum;
    }, 0);

    const totalAdvance = advancePaid + metalAdvance;

    return { subtotal, totalAdvance, metalCredit };
  };

  const totals = calculateTotals();

  // Group payments by mode
  const groupPaymentsByMode = () => {
    const grouped = {};
    (order.advance_payments || []).forEach(payment => {
      const mode = payment.mode || 'Unknown';
      if (!grouped[mode]) {
        grouped[mode] = [];
      }
      grouped[mode].push(payment);
    });
    return grouped;
  };

  const paymentsByMode = groupPaymentsByMode();

  // Format date
  const formatDate = (dateStr) => {
    if (!dateStr) return 'N/A';
    const date = new Date(dateStr);
    return date.toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  };

  // Format amount in Indian comma format
  const formatIndianCurrency = (amount) => {
    const num = parseFloat(amount) || 0;
    return num.toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  // Convert number to words
  const numberToWords = (num) => {
    if (!num || num === 0) return 'Zero Rupees';
    
    const ones = ['', 'One', 'Two', 'Three', 'Four', 'Five', 'Six', 'Seven', 'Eight', 'Nine'];
    const tens = ['', '', 'Twenty', 'Thirty', 'Forty', 'Fifty', 'Sixty', 'Seventy', 'Eighty', 'Ninety'];
    const teens = ['Ten', 'Eleven', 'Twelve', 'Thirteen', 'Fourteen', 'Fifteen', 'Sixteen', 'Seventeen', 'Eighteen', 'Nineteen'];
    
    const convertLessThanThousand = (n) => {
      if (n === 0) return '';
      if (n < 10) return ones[n];
      if (n < 20) return teens[n - 10];
      if (n < 100) return tens[Math.floor(n / 10)] + (n % 10 !== 0 ? ' ' + ones[n % 10] : '');
      return ones[Math.floor(n / 100)] + ' Hundred' + (n % 100 !== 0 ? ' ' + convertLessThanThousand(n % 100) : '');
    };
    
    const absNum = Math.abs(Math.floor(num));
    let result = '';
    
    if (absNum >= 10000000) {
      const crores = Math.floor(absNum / 10000000);
      result += convertLessThanThousand(crores) + ' Crore ';
      num = absNum % 10000000;
    } else {
      num = absNum;
    }
    
    if (num >= 100000) {
      const lakhs = Math.floor(num / 100000);
      result += convertLessThanThousand(lakhs) + ' Lakh ';
      num = num % 100000;
    }
    
    if (num >= 1000) {
      const thousands = Math.floor(num / 1000);
      result += convertLessThanThousand(thousands) + ' Thousand ';
      num = num % 1000;
    }
    
    if (num > 0) {
      result += convertLessThanThousand(num);
    }
    
    return result.trim() + ' Rupees Only';
  };

  const calculateModeSubtotal = (payments, isMetal = false) => {
    return payments.reduce((sum, p) => {
      if (isMetal) {
        // For Metal Credit, sum up fine_wt from deposits or directly
        if (p.deposits && Array.isArray(p.deposits) && p.deposits.length > 0) {
          return sum + p.deposits.reduce((depSum, dep) => depSum + parseFloat(dep.fine_wt || 0), 0);
        }
        return sum + parseFloat(p.fine_wt || p.fine_grams || 0);
      }
      const amount = parseFloat(p.amount) || 0;
      return p.transaction_type === 'REFUND' ? sum - amount : sum + amount;
    }, 0);
  };

  // Calculate total gross weight
  const calculateTotalGrossWeight = () => {
    let total = 0;
    order.items?.forEach(item => {
      const weight = item.weight_type === 'Fixed' 
        ? parseFloat(item.weight_fixed || 0) 
        : parseFloat(item.weight_min || 0); // Use min for range
      total += weight;
    });
    return total.toFixed(2);
  };

  // Get unique purities with rates
  const getUniquePuritiesWithRates = () => {
    const purities = {};
    order.items?.forEach(item => {
      if (item.purity && item.rate) {
        purities[item.purity] = item.rate;
      }
    });
    return purities;
  };

  const puritiesWithRates = getUniquePuritiesWithRates();

  if (!order) return null;

  return (
    <div id="thermal-summary-print-content" className="print-content-hidden">
      {order?.has_provisional_changes && <div className="provisional-watermark">PROVISIONAL</div>}
      <div style={{
        width: '80mm',
        padding: '2mm',
        fontFamily: "'Courier New', monospace",
        fontSize: '16px',
        lineHeight: '1.15',
        color: '#000',
        wordWrap: 'break-word'
      }}>
        
        {/* Header */}
        <div style={{ textAlign: 'center', marginBottom: '3px', borderBottom: '2px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>CUSTOMER ORDER</div>
          <div style={{ fontSize: '13px', fontWeight: 'bold' }}>Order ID: {order.order_id}</div>
          <div style={{ fontSize: '16px' }}>Date: {formatDate(order.order_date || order.created_at)}</div>
        </div>

        {/* Customer Information */}
        <div style={{ marginBottom: '3px', borderBottom: '1px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '1px' }}>CUSTOMER INFORMATION</div>
          {customer && (
            <>
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>Name:</strong> {customer.name}
              </div>
              {customer.mobile && (
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                  <strong>Mobile:</strong> {customer.mobile}
                </div>
              )}
              {customer.alternate_mobile && (
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                  <strong>Alt Mobile:</strong> {customer.alternate_mobile}
                </div>
              )}
              {customer.email && (
                <div style={{ fontSize: '16px', marginBottom: '2px', wordBreak: 'break-all' }}>
                  <strong>Email:</strong> {customer.email}
                </div>
              )}
              {customer.address && (
                <div style={{ fontSize: '16px', marginBottom: '2px', wordWrap: 'break-word' }}>
                  <strong>Address:</strong> {customer.address}
                </div>
              )}
            </>
          )}
        </div>

        {/* Rate Policy & Base Rate */}
        {(order.rate_policy || order.base24k_rate_ex_gst) && (
          <div style={{ marginBottom: '3px', borderBottom: '1px solid #000', paddingBottom: '2px', backgroundColor: '#fffef0' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '1px' }}>RATE INFORMATION</div>
            {order.rate_policy && (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>Rate Policy:</strong> {
                  order.rate_policy === 'lock-today' ? 'Rate Booked Today' :
                  order.rate_policy === 'rate-at-delivery' ? 'Rate at Delivery' :
                  'None'
                }
              </div>
            )}
            {order.rate_policy === 'lock-today' && order.base24k_rate_ex_gst && (
              <>
                <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                  <strong>24K Base Rate:</strong> ₹{formatIndianCurrency(order.base24k_rate_ex_gst)}/g
                </div>
                {order.lock_date && (
                  <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                    <strong>Lock Date:</strong> {formatDate(order.lock_date)}
                  </div>
                )}
              </>
            )}
            {order.gst_applicable === false ? (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>GST:</strong> Kaccha (No GST)
              </div>
            ) : (
              <div style={{ fontSize: '16px', marginBottom: '2px' }}>
                <strong>GST:</strong> {order.gst_percent_snapshot || 3}% {order.gst_input_style === 'INCL' ? '(Inclusive)' : '(Exclusive)'}
              </div>
            )}
          </div>
        )}

        {/* Order Summary */}
        <div style={{ marginBottom: '3px', borderBottom: '1px solid #000', paddingBottom: '2px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '1px' }}>ORDER SUMMARY</div>
          <div style={{ fontSize: '16px', marginBottom: '2px' }}>
            <strong>Total Items:</strong> {order.items?.length || 0} pieces
          </div>
          <div style={{ fontSize: '16px', marginBottom: '2px' }}>
            <strong>Total Gross Weight:</strong> {calculateTotalGrossWeight()}g
          </div>

          {/* Items List */}
          <div style={{ fontSize: '9px', marginTop: '4px' }}>
            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Items Ordered:</div>
            {order.items?.map((item, idx) => {
              const weight = item.weight_type === 'Fixed' 
                ? `${item.weight_fixed}g` 
                : `${item.weight_min}g`;
              const qty = `${item.quantity || 1} piece${parseInt(item.quantity || 1) > 1 ? 's' : ''}`;
              const delivery = item.ready_item ? 'READY STOCK' : (item.delivery_date ? `Del: ${formatDate(item.delivery_date)}` : '');
              
              return (
                <div key={idx} style={{ marginBottom: '2px', lineHeight: '1.5' }}>
                  • {item.item_type || 'Item'} ({item.purity || '22K'}) - {weight} - {qty}{delivery ? ` - ${delivery}` : ''}
                </div>
              );
            })}
          </div>

          {/* Rate Card - Only show if rate_mode is 'single' and showItemRate is enabled */}
          {showItemRate && order.rate_mode !== 'per-item' && Object.keys(puritiesWithRates).length > 0 && (
            <div style={{ fontSize: '9px', marginTop: '4px', paddingTop: '2px', borderTop: '1px dashed #000' }}>
              <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Rate Card:</div>
              {Object.keys(puritiesWithRates).sort().map((purity, idx) => (
                <div key={idx} style={{ marginBottom: '1px' }}>
                  {purity} Rate: ₹{formatIndianCurrency(puritiesWithRates[purity])}/g
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Payments & Deposits */}
        {order.advance_payments && order.advance_payments.length > 0 && (
          <div style={{ marginBottom: '3px', borderTop: '2px solid #000', paddingTop: '6px' }}>
            <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>
              💳 PAYMENTS & DEPOSITS
            </div>
            
            {Object.keys(paymentsByMode).map((mode, modeIdx) => (
              <div key={modeIdx} style={{ marginBottom: '3px' }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '1px', textTransform: 'uppercase' }}>
                  {mode}
                </div>
                
                {/* Special handling for Old Gold */}
                {mode === 'Old Gold' ? (
                  <>
                    {paymentsByMode[mode].map((payment, payIdx) => (
                      <div key={payIdx} style={{ fontSize: '9px', marginBottom: '2px', border: '1px solid #000', padding: '3px' }}>
                        <div style={{ marginBottom: '1px' }}>
                          <strong>Date:</strong> {formatDate(payment.date)}
                        </div>
                        <div style={{ marginBottom: '1px' }}>
                          <strong>Amount:</strong> ₹{formatIndianCurrency(payment.amount || 0)}
                        </div>
                        
                        {payment.items && payment.items.length > 0 && (
                          <div style={{ marginTop: '4px', borderTop: '1px dashed #000', paddingTop: '3px' }}>
                            <div style={{ fontWeight: 'bold', marginBottom: '2px' }}>Item Details:</div>
                            {payment.items.map((item, itemIdx) => (
                              <div key={itemIdx} style={{ marginLeft: '4px', marginBottom: '1px', paddingLeft: '4px', borderLeft: '2px solid #000' }}>
                                {item.item_name && (
                                  <div><strong>Item:</strong> {item.item_name}</div>
                                )}
                                {item.gross_wt && (
                                  <div><strong>Gross Wt:</strong> {item.gross_wt}g</div>
                                )}
                                {item.less_wt && (
                                  <div><strong>Less Wt:</strong> {item.less_wt}g</div>
                                )}
                                {item.net_wt && (
                                  <div><strong>Net Wt:</strong> {item.net_wt}g</div>
                                )}
                                {item.purity && (
                                  <div><strong>Purity:</strong> {item.purity}%</div>
                                )}
                                {item.fine_wt && (
                                  <div><strong>Fine Wt:</strong> {item.fine_wt}g</div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                        
                        {payment.note && (
                          <div style={{ marginTop: '3px', wordWrap: 'break-word' }}>
                            <strong>Note:</strong> {payment.note}
                          </div>
                        )}
                      </div>
                    ))}
                    <div style={{ 
                      fontSize: '9px', 
                      fontWeight: 'bold', 
                      textAlign: 'right', 
                      marginBottom: '2px',
                      paddingTop: '2px',
                      borderTop: '1px solid #000'
                    }}>
                      Old Gold Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                ) : mode === 'Metal Credit' ? (
                  /* Metal Credit - COMPACT Format matching detailed */
                  <>
                    {paymentsByMode[mode]
                      .filter(p => parseFloat(p.fine_wt || p.fine_grams || 0) > 0 || (p.deposits && p.deposits.length > 0))
                      .map((payment, payIdx) => {
                        const hasDeposits = payment.deposits && Array.isArray(payment.deposits) && payment.deposits.length > 0;
                        
                        return (
                          <div key={payIdx} style={{ fontSize: '14px', marginBottom: '2px', border: '2px solid #000', padding: '2px', backgroundColor: '#f9f9f9', fontWeight: '700', pageBreakInside: 'avoid' }}>
                            {hasDeposits ? (
                              <>
                                {payment.deposits.map((deposit, depIdx) => (
                                  <div key={depIdx} style={{ marginBottom: '1px', paddingBottom: '2px', borderBottom: depIdx < payment.deposits.length - 1 ? '1px dashed #000' : 'none', pageBreakInside: 'avoid' }}>
                                    <div style={{ fontWeight: '900', fontSize: '12px', marginBottom: '1px', textTransform: 'uppercase' }}>
                                      Deposit {depIdx + 1}{deposit.item_name && `: ${deposit.item_name}`}
                                    </div>
                                    
                                    {/* Compact list format */}
                                    <div style={{ fontSize: '14px', lineHeight: '1.15', fontWeight: '700' }}>
                                      <div><strong>Date:</strong> {formatDate(deposit.date || payment.date)}</div>
                                      {(deposit.type || deposit.type_name_other) && (
                                        <div><strong>Type:</strong> {deposit.type_name_other || deposit.type}</div>
                                      )}
                                      <div><strong>Gross:</strong> {deposit.gross_wt || deposit.net_wt || 0}g{deposit.less_wt ? ` | Less: ${deposit.less_wt}g` : ''}</div>
                                      <div><strong>Net:</strong> {deposit.net_wt || 0}g | <strong>Purity:</strong> {deposit.purity_label || `${deposit.purity}%`}</div>
                                      <div style={{ fontSize: '12px', fontWeight: '900' }}><strong>Fine Wt:</strong> {deposit.fine_wt || 0}g</div>
                                    </div>
                                    
                                    {deposit.reference && (
                                      <div style={{ marginTop: '1px', wordWrap: 'break-word', fontSize: '14px', fontWeight: '700' }}>
                                        <strong>Ref:</strong> {deposit.reference}
                                      </div>
                                    )}
                                  </div>
                                ))}
                              </>
                            ) : (
                              /* Single metal credit without deposits array */
                              <div style={{ fontSize: '14px', lineHeight: '1.15', fontWeight: '700' }}>
                                <div><strong>Date:</strong> {formatDate(payment.date)}</div>
                                <div style={{ fontSize: '12px', fontWeight: '900' }}><strong>Fine Wt:</strong> {payment.fine_wt || payment.fine_grams || 0}g</div>
                                {payment.reference && (
                                  <div><strong>Ref:</strong> {payment.reference}</div>
                                )}
                              </div>
                            )}
                            
                            {/* Metal Credit Total for this payment */}
                            <div style={{ marginTop: '2px', paddingTop: '2px', borderTop: '1px solid #000', fontWeight: '900', fontSize: '12px' }}>
                              Metal Credit: {hasDeposits 
                                ? payment.deposits.reduce((sum, dep) => sum + parseFloat(dep.fine_wt || 0), 0).toFixed(3) 
                                : parseFloat(payment.fine_wt || payment.fine_grams || 0).toFixed(3)}g
                            </div>
                          </div>
                        );
                      })}
                    
                    {/* Total Metal Credit */}
                    <div style={{ marginTop: '2px', fontWeight: '900', fontSize: '14px', textAlign: 'right', padding: '2px', backgroundColor: '#e0e0e0', border: '2px solid #000' }}>
                      Metal Credit Total: {calculateModeSubtotal(paymentsByMode[mode], true).toFixed(3)}g
                    </div>
                  </>
                ) : (
                  <>
                    <div style={{ border: '1px solid #000', marginBottom: '2px' }}>
                      <table style={{ width: '100%', fontSize: '9px', borderCollapse: 'collapse' }}>
                        <thead>
                          <tr style={{ borderBottom: '1px solid #000' }}>
                            <th style={{ padding: '2px', textAlign: 'left', borderRight: '1px solid #ddd' }}>Date</th>
                            <th style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #ddd' }}>Amount</th>
                            <th style={{ padding: '2px', textAlign: 'left' }}>Details</th>
                          </tr>
                        </thead>
                        <tbody>
                          {paymentsByMode[mode].map((payment, payIdx) => {
                            const amount = parseFloat(payment.amount) || 0;
                            const isRefund = payment.transaction_type === 'REFUND';
                            const displayAmount = isRefund ? -amount : amount;
                            
                            return (
                              <tr key={payIdx} style={{ borderBottom: payIdx < paymentsByMode[mode].length - 1 ? '1px solid #ddd' : 'none' }}>
                                <td style={{ padding: '2px', borderRight: '1px solid #ddd', fontSize: '8px' }}>
                                  {formatDate(payment.date)}
                                </td>
                                <td style={{ padding: '2px', textAlign: 'right', borderRight: '1px solid #ddd', color: isRefund ? '#d00' : '#000' }}>
                                  {isRefund ? '-' : ''}₹{formatIndianCurrency(Math.abs(displayAmount))}
                                </td>
                                <td style={{ padding: '2px', fontSize: '8px', wordWrap: 'break-word' }}>
                                  {isRefund && <div style={{ color: '#d00', fontWeight: 'bold' }}>REFUND</div>}
                                  {payment.mode === 'UPI' && payment.upi_ref && (
                                    <div>Ref: {payment.upi_ref}</div>
                                  )}
                                  {payment.mode === 'Card' && payment.card_last4 && (
                                    <div>****{payment.card_last4}</div>
                                  )}
                                  {(payment.mode === 'IMPS' || payment.mode === 'NEFT/RTGS') && payment.utr_ref && (
                                    <div>UTR: {payment.utr_ref}</div>
                                  )}
                                  {payment.mode === 'Cheque' && payment.cheque_no && (
                                    <div>#{payment.cheque_no}</div>
                                  )}
                                  {payment.note && (
                                    <div style={{ fontSize: '8px', color: '#666' }}>{payment.note}</div>
                                  )}
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{ 
                      fontSize: '9px', 
                      fontWeight: 'bold', 
                      textAlign: 'right', 
                      marginBottom: '2px',
                      paddingTop: '2px',
                      borderTop: '1px solid #000'
                    }}>
                      {mode} Total: ₹{formatIndianCurrency(calculateModeSubtotal(paymentsByMode[mode]))}
                    </div>
                  </>
                )}
              </div>
            ))}
            
            {/* Net Advance Total */}
            <div style={{ 
              marginTop: '2px', 
              border: '3px double #000'
            }}>
              <div style={{
                padding: '2px',
                textAlign: 'center',
                backgroundColor: '#f9f9f9',
                borderBottom: totals.metalCredit > 0 ? '2px solid #000' : 'none'
              }}>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>
                  NET ADVANCE PAID
                </div>
                <div style={{ fontSize: '16px', fontWeight: 'bold', marginBottom: '2px' }}>
                  ₹{formatIndianCurrency(totals.totalAdvance)}
                </div>
                <div style={{ fontSize: '8px', fontStyle: 'italic', wordWrap: 'break-word' }}>
                  ({numberToWords(totals.totalAdvance)})
                </div>
              </div>
              
              {totals.metalCredit > 0 && (
                <div style={{
                  padding: '2px',
                  textAlign: 'center',
                  backgroundColor: '#fff9e6'
                }}>
                  <div style={{ fontSize: '13px', fontWeight: 'bold' }}>
                    METAL CREDIT
                  </div>
                  <div style={{ fontSize: '16px', fontWeight: 'bold' }}>
                    {totals.metalCredit.toFixed(2)}g fine gold
                  </div>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Terms & Conditions */}
        <div style={{ marginBottom: '3px', borderTop: '2px solid #000', paddingTop: '6px' }}>
          <div style={{ fontSize: '13px', fontWeight: 'bold', marginBottom: '2px' }}>TERMS & CONDITIONS</div>
          {termsAndConditions.length > 0 ? (
            <div style={{ fontSize: '8px', lineHeight: '1.6' }}>
              {termsAndConditions.map((term, idx) => (
                <div key={idx} style={{ marginBottom: '1px', wordWrap: 'break-word' }}>
                  • {term.text || term.content || term}
                </div>
              ))}
            </div>
          ) : (
            <div style={{ fontSize: '8px', fontStyle: 'italic', color: '#666' }}>
              No terms and conditions configured.
            </div>
          )}
        </div>

        {/* Footer Spacing */}
        <div style={{ height: '10mm' }}></div>

      </div>
    </div>
  );
};

export default OrderPrintThermalSummary;
